import type { Line } from './howToPlay';
import type { Board, GameType, MarkedCell } from './types';

/*
  A game's guided tutorial, as data.

  The same split as How to Play: each game package says what every step asks of the player, what it
  points at on the board, and whether it's done. Each platform owns one hook that walks the steps and
  one renderer that draws the message. Nothing here is a component, a class name or a callback — the
  Next and Reset buttons belong to the renderer, which knows where they go.
*/

/** A small cell drawn inline with the text, so copy can name a field by showing it. */
export interface TutorialSwatch {
  kind: 'swatch';
  /** Resolved colour values, as in `CycleCell`. */
  fill: string;
  ink?: string;
  label?: string;
}

/** A paragraph: a plain line, or lines interleaved with swatches. */
export type TutorialText = Line | (Line | TutorialSwatch)[];

/** A checklist, ticked off as the player does what each item asks. */
export interface TutorialTasks {
  kind: 'tasks';
  tasks: { text: TutorialText; done: boolean }[];
}

export type TutorialBlock = TutorialText | TutorialTasks;

export type TutorialMessage =
  | {
      kind: 'instruction';
      heading?: Line;
      blocks: TutorialBlock[];
      /** Whether Next is enabled: the step's work is done, or it never had any. */
      done: boolean;
      nextLabel?: string;
      /** Defaults to true. Off for steps that end on their own, where there's never anything to go on to. */
      showNext?: boolean;
      /** Defaults to true. Off for steps with nothing on the board to undo. */
      showReset?: boolean;
    }
  /** The finish: the renderer's own success panel, with optional game-specific copy. */
  | { kind: 'complete'; title?: Line; description?: Line };

/** One state of a demo: what the board holds and marks, for `hold` milliseconds. */
export interface TutorialDemoFrame<CellT = unknown> {
  cells: CellT[][];
  marks?: MarkedCell[];
  hold: number;
}

/**
 * A rule acted out on the game's own board, for a step that demonstrates a rule rather than asking the
 * player to work on it: a board of its own, stepped through a loop of states, with the board's own
 * animations playing between them. The game's board renderer is the only thing that reads it, so it's
 * typed in the game's own terms there.
 */
export interface TutorialDemo<BoardT = unknown, CellT = unknown> {
  /** What it shows, for a screen reader. */
  alt: string;
  board: BoardT;
  frames: TutorialDemoFrame<CellT>[];
}

export interface TutorialStep {
  message: TutorialMessage;
  /** Marks drawn on the board. Defaults to none. */
  marks?: MarkedCell[];
  /** Whether the board takes input. Defaults to true; a game may also lock cells with `disabled` marks. */
  canInteract?: boolean;
  /** Played on a board of its own in the play board's place, which the step then never shows. */
  demo?: TutorialDemo;
}

/** What a step reads to decide what to say. The steps are pure functions of this. */
export interface TutorialContext<BoardT extends Board, CellT> {
  board: BoardT;
  cells: CellT[][];
  complete: boolean;
  /**
   * This step's own last result, while the player stays on it. A step that shouldn't change its mind
   * on every move — one that follows a hint through to the end rather than jumping to another halfway
   * — decides from here whether to keep it.
   */
  previous?: TutorialStep;
}

/**
 * A stretch of steps played on one board. Entering a phase at its first step starts from a blank
 * board, and Reset goes back to the start of the current phase.
 */
export interface TutorialPhase {
  slug: string;
  firstStep: number;
}

export interface Tutorial<BoardT extends Board, CellT> {
  game: GameType;
  /** In step order, the first starting at step 0. */
  phases: TutorialPhase[];
  steps: ((context: TutorialContext<BoardT, CellT>) => TutorialStep)[];
}

/** The phase a step belongs to. */
export function tutorialPhase<BoardT extends Board, CellT>(
  tutorial: Tutorial<BoardT, CellT>,
  step: number
): TutorialPhase {
  return [...tutorial.phases].reverse().find((phase) => phase.firstStep <= step) ?? tutorial.phases[0];
}

/** The step named by a URL parameter, clamped to the tutorial; anything unreadable is step 0. */
export function tutorialStepFromParam<BoardT extends Board, CellT>(
  tutorial: Tutorial<BoardT, CellT>,
  param: string | null | undefined
): number {
  const parsed = param != null ? Number.parseInt(param, 10) : 0;
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(parsed, tutorial.steps.length - 1);
}

/** Every piece of a paragraph, whichever form it was written in. */
export function tutorialTextParts(text: TutorialText): (Line | TutorialSwatch)[] {
  return typeof text === 'string' ? [text] : text;
}

export function isTutorialTasks(block: TutorialBlock): block is TutorialTasks {
  return typeof block === 'object' && !Array.isArray(block);
}
